import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Target, ArrowLeft, Plus, Trash2, Circle, CheckCircle } from 'lucide-react';
import { getChildTheme } from '../childThemes';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const ChildGoalsPage = () => {
  const [child, setChild] = useState(null);
  const [theme, setTheme] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [goals, setGoals] = useState([]);
  const [newGoalText, setNewGoalText] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const childUser = localStorage.getItem('childUser');
    const childToken = localStorage.getItem('childToken');
    
    if (!childUser || !childToken) {
      navigate('/child/login');
      return;
    }
    
    // Set API token from childToken for authenticated requests
    api.setToken(childToken);
    
    const childData = JSON.parse(childUser);
    setChild(childData);
    const childTheme = getChildTheme(childData.theme || 'purple');
    setTheme(childTheme);
    
    // Load photo from localStorage immediately for instant display
    const cachedPhotoUrl = localStorage.getItem(`child_photo_${childData.id}`);
    if (cachedPhotoUrl && cachedPhotoUrl !== 'null') {
      setProfilePhoto(cachedPhotoUrl);
    }
    
    loadGoals(childData.id);
  }, [navigate]);

  const loadGoals = (childId) => {
    // Load from localStorage for now (student-specific goals)
    const storageKey = `child_goals_${childId}`;
    const savedGoals = localStorage.getItem(storageKey);
    if (savedGoals) {
      setGoals(JSON.parse(savedGoals));
    }
  };

  const saveGoals = (updatedGoals) => {
    const storageKey = `child_goals_${child.id}`;
    localStorage.setItem(storageKey, JSON.stringify(updatedGoals));
    setGoals(updatedGoals);
  };

  const handleAddGoal = () => {
    if (!newGoalText.trim()) {
      toast({
        title: 'Empty Goal',
        description: 'Please enter a goal',
        variant: 'destructive'
      });
      return;
    }

    const newGoal = {
      id: Date.now().toString(),
      text: newGoalText,
      completed: false,
      createdAt: new Date().toISOString()
    };

    const updatedGoals = [...goals, newGoal];
    saveGoals(updatedGoals);
    setNewGoalText('');
    
    toast({
      title: 'Goal Added!',
      description: 'Keep working towards your goal!'
    });
  };

  const handleToggleGoal = (goalId) => {
    const updatedGoals = goals.map(g => 
      g.id === goalId ? { ...g, completed: !g.completed } : g
    );
    saveGoals(updatedGoals);
  };

  const handleDeleteGoal = (goalId) => {
    const updatedGoals = goals.filter(g => g.id !== goalId);
    saveGoals(updatedGoals);
    
    toast({
      title: 'Goal Removed',
      description: 'Goal has been deleted'
    });
  };

  if (!child || !theme) return null;

  const completedCount = goals.filter(g => g.completed).length;
  const totalCount = goals.length;

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.cardBg}`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${theme.headerBg} text-white p-6 shadow-lg`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/child/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            {/* Profile Photo */}
            <div
              className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-xl font-bold shadow-lg overflow-hidden cursor-pointer"
              style={{ color: child.profileColor }}
              onClick={() => navigate('/child/dashboard')}
            >
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                child.name.charAt(0)
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
              <Target className="w-10 h-10" />
            </div>
            <div>
              <h1 className="text-4xl font-bold">My Goals</h1>
              <p className={`${theme.accentColor === 'purple-600' ? 'text-purple-100' : 'text-blue-100'}`}>
                Track your learning achievements
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Progress Summary */}
        <Card className="p-6 border-0 shadow-lg bg-white mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Your Progress</h2>
              <p className="text-slate-600 mt-1">
                {completedCount} of {totalCount} goals completed
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600">
                {totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0}%
              </div>
              <p className="text-sm text-slate-500">Complete</p>
            </div>
          </div>
        </Card>

        {/* Add New Goal */}
        <Card className="p-6 border-0 shadow-lg bg-white mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Add a New Goal</h2>
          <div className="flex gap-3">
            <Input
              value={newGoalText}
              onChange={(e) => setNewGoalText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddGoal()}
              placeholder="What do you want to achieve?"
              className="flex-1"
            />
            <Button
              onClick={handleAddGoal}
              className={`bg-gradient-to-r ${theme.buttonPrimary} text-white`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Goal
            </Button>
          </div>
        </Card>

        {/* Goals List */}
        <div className="space-y-3">
          {goals.map(goal => (
            <Card
              key={goal.id}
              className={`p-4 border-0 shadow-lg transition-all ${
                goal.completed ? 'bg-green-50' : 'bg-white'
              }`}
            >
              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleToggleGoal(goal.id)}
                  className="flex-shrink-0"
                >
                  {goal.completed ? (
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  ) : (
                    <Circle className="w-8 h-8 text-slate-300 hover:text-slate-400" />
                  )}
                </button>
                
                <div className="flex-1">
                  <p className={`text-lg ${goal.completed ? 'line-through text-slate-500' : 'text-slate-900'}`}>
                    {goal.text}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Added {new Date(goal.createdAt).toLocaleDateString()}
                  </p>
                </div>

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDeleteGoal(goal.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {goals.length === 0 && (
          <Card className="p-12 border-0 shadow-lg bg-white text-center">
            <Target className="w-20 h-20 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">No Goals Yet</h2>
            <p className="text-slate-600">
              Add your first goal above to start tracking your achievements!
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ChildGoalsPage;
